import { createContext, useContext, useEffect, useRef } from 'react'
import type { Locale } from '@diana/shared'
import type { Solution } from '../../../lib/api.js'
import type { PlayerStrings } from './copy.js'

/*
 * What a step can ask of the player frame around it: the one primary
 * action in the bottom bar, the feedback strip above it, and moving on.
 * Exercise answers live here too, keyed by exercise, so going back to a step
 * never loses what was typed ("Back never loses answers").
 */

export type Primary = { label: string; disabled?: boolean; onClick: () => void; skip?: boolean }
export type Strip = { tone: 'ok' | 'bad' | 'neutral'; text: string; score?: string; action?: { label: string; onClick: () => void } }

export type CheckResult = { correct: boolean[]; score: number; max: number; solution: Solution }
export type ExerciseMemory = {
  answers: string[]
  result: CheckResult | null
  /** Items already right in an earlier round of „Popraw błędne”. */
  locked: boolean[]
}

export type PlayerApi = {
  locale: Locale
  t: PlayerStrings
  next: () => void
  goTo: (index: number) => void
  setPrimary: (p: Primary | null) => void
  /** A step with pages of its own (word cards) handles „Wstecz” first; true = handled. */
  setBack: (fn: (() => boolean) | null) => void
  /** "3 / 22" beside the phone's progress bar. */
  setCounter: (text: string | null) => void
  setStrip: (s: Strip | null) => void
  memory: Map<string, ExerciseMemory>
  /** Diana's teacher view; absent for a student. */
  teacher?: TeacherState | null
}

/**
 * The teacher view (Studio → plan → „Prowadź lekcję”). With the key shown,
 * every exercise is read-only: the chosen student's answers, marked, and
 * the key under them. With the key hidden — while she shares her screen —
 * the player behaves as the student's, their answers filled in, so the two
 * of them can check together.
 */
export type TeacherState = { keyShown: boolean; student: string | null }

export const PlayerContext = createContext<PlayerApi | null>(null)

export function usePlayer(): PlayerApi {
  const api = useContext(PlayerContext)
  if (!api) throw new Error('usePlayer outside the lesson player')
  return api
}

/**
 * Declares the bottom bar's primary action for the step that calls it. The
 * click handler is read through a ref, so a new closure every render does
 * not re-render the frame.
 */
export function usePrimary(spec: Primary | null): void {
  const { setPrimary } = usePlayer()
  const click = useRef(spec?.onClick)
  click.current = spec?.onClick
  const label = spec?.label
  const disabled = spec?.disabled
  const skip = spec?.skip
  useEffect(() => {
    setPrimary(label === undefined ? null : { label, disabled, skip, onClick: () => click.current?.() })
  }, [label, disabled, skip, setPrimary])
  useEffect(() => () => setPrimary(null), [setPrimary])
}

/** The feedback strip above the bottom bar; `key` decides when it is redrawn. */
export function useStrip(strip: Strip | null, key: string): void {
  const { setStrip } = usePlayer()
  const action = useRef(strip?.action?.onClick)
  action.current = strip?.action?.onClick
  useEffect(() => {
    setStrip(
      strip
        ? { ...strip, action: strip.action ? { label: strip.action.label, onClick: () => action.current?.() } : undefined }
        : null,
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, setStrip])
  useEffect(() => () => setStrip(null), [setStrip])
}

/** Lets a step page backwards inside itself before the player leaves it. */
export function useBack(handler: (() => boolean) | null): void {
  const { setBack } = usePlayer()
  const ref = useRef(handler)
  ref.current = handler
  const has = handler !== null
  useEffect(() => {
    setBack(has ? () => ref.current?.() ?? false : null)
    return () => setBack(null)
  }, [has, setBack])
}

export function useCounter(text: string | null): void {
  const { setCounter } = usePlayer()
  useEffect(() => {
    setCounter(text)
    return () => setCounter(null)
  }, [text, setCounter])
}

/*
 * Homework shows its parts stacked on one page instead of one per step. Each
 * part reports its state up and hands over its check, so the page's one
 * primary action can check every answered part at once.
 */
export type PartState = { answered: boolean; checked: boolean; done: boolean; score: number; max: number; wrong: number }
export type StackApi = {
  register: (id: string, check: () => void) => () => void
  report: (id: string, state: PartState) => void
}
export const StackContext = createContext<StackApi | null>(null)
export function useStack(): StackApi | null {
  return useContext(StackContext)
}
